
import { useState, useEffect } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useToast } from "@/hooks/use-toast";
import { Lead } from '@/types/lead';

interface UseLeadsSearchProps {
  allLeads: Lead[];
}

// PERFORMANCE: Debounced search prevents filtering on every keystroke
export const useLeadsSearch = ({ allLeads }: UseLeadsSearchProps) => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>(allLeads);
  
  // PERFORMANCE: Debounce search query by 300ms to reduce re-renders
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Filter leads client-side by `name` as the user types (debounced)
  useEffect(() => {
    if (!debouncedSearchQuery) {
      setFilteredLeads(allLeads);
    } else {
      const filtered = allLeads.filter(lead =>
        lead.name?.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
      );
      setFilteredLeads(filtered);
    }
  }, [debouncedSearchQuery, allLeads]);

  const clearSearch = () => setSearchQuery('');

  return {
    searchQuery,
    setSearchQuery,
    filteredLeads,
    clearSearch
  };
};
